import { useEffect, useState, useRef } from "react";
import { Avatar, Button, Card, Col, Typography, Input, Row, Spin, Flex } from "antd";
import { SendOutlined, LeftOutlined } from "@ant-design/icons";
import { useIsMobile } from "hooks/useIsMobile";
import useChatRooms from "api/messages/useChatRooms";
import sendMessage from "api/messages/useSendMessage";
import { storage } from "utils/storage";
import updateMessagesStatus from "api/messages/updateMessageStatus";
import EmptyMessageSVG from "assets/message.svg";
import getSenderInfo from "api/messages/getSenderInfo";

const { Title, Text } = Typography;

const Messages = () => {
  const user_id = storage.getUserFromStorage().uid;
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const [userNames, setUserNames] = useState<{ [key: string]: string }>({});
  const [optimisticMessages] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { chatRooms, loading: chatRoomsLoading } = useChatRooms(user_id);


  useEffect(() => {
    if (selectedJobId && !selectedUserId) {
      const selectedChatRoom = chatRooms.find((room) => room.jobId === selectedJobId);
      if (selectedChatRoom) {

        const users = new Set<string>();
        selectedChatRoom.messages.forEach((msg) => {
          const otherUser = msg.sender === user_id ? msg.recipient : msg.sender;
          if (otherUser !== user_id) users.add(otherUser);
        });
        const userList = Array.from(users);
        userList.forEach((userId) => {
          if (!userNames[userId]) {
            getSenderInfo(userId).then((name) => {
              setUserNames((prev) => ({ ...prev, [userId]: name }));
            });
          }
        });
      }
    }
  }, [selectedJobId, selectedUserId, chatRooms, userNames, user_id]);

  useEffect(() => {
    if (selectedUserId) {
      updateMessagesStatus(selectedUserId, user_id);
    }

  }, [selectedUserId, user_id, chatRooms]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
    }
  }, [chatRooms, selectedUserId, selectedJobId, optimisticMessages]);

  const handleSendMessage = async () => {
    if (!message || !selectedJobId || !selectedUserId) return;

    setLoading(true);
    const messageText = message;
    setMessage("");
    try {
      await sendMessage({
        sender: user_id,
        jobId: selectedJobId,
        recipient: selectedUserId,
        text: messageText,
        companyName: chatRooms.find((room) => room.jobId === selectedJobId)?.companyName || "",
        status: "unread",
        jobTitle: chatRooms.find((room) => room.jobId === selectedJobId)?.jobTitle || "",
      });


    } catch (error) {
      console.error("Message failed to send:", error);
    } finally {
      setLoading(false);
    }
  };


  // Render the list of jobs
  const renderUsers = () => (
    <div className="h-full bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b">
        <Title level={4} className="!mb-0">
          Messages
        </Title>
      </div>
      <div className="h-[calc(100vh-220px)] overflow-y-auto">
        {chatRoomsLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spin size="large" />
          </div>
        ) : (
          chatRooms.map((room) => (
            <Card
              key={room.jobId}
              className={`!rounded-none !border-0 !cursor-pointer !transition-colors
                ${room.jobId === selectedJobId ? "bg-secondary" : "hover:!bg-gray-50"}`}
              onClick={() => {
                setSelectedJobId(room.jobId);
                setSelectedUserId(null);
              }}
            >
              <Flex gap="middle" align="center">
                <Avatar size="large" className="!bg-primary-500 !text-white">
                  {room.jobTitle[0]}
                </Avatar>
                <Flex vertical className="w-[calc(100%-56px)]">
                  <Title level={5} className="!mb-0 truncate">
                    <Flex align="center">
                      {room.messages.some(
                        (msg) => msg.sender !== user_id && msg.status === "unread"
                      ) && <div className="w-2 h-2 rounded-full bg-red-500 mr-2" />}
                      {room.jobTitle}
                    </Flex>
                  </Title>
                  <Text type="secondary" className="truncate">
                    {room.companyName}
                  </Text>
                </Flex>
              </Flex>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  const renderMessages = () => {
    if (!selectedJobId) {
      return (
        <Flex vertical align="center" justify="center" className="h-full p-4 text-center">
          <img src={EmptyMessageSVG} alt="No messages" className="w-48 h-48 mb-6" />
          <Title level={4} className="!mb-2">
            Welcome to your messages
          </Title>
          <Text type="secondary" className="max-w-md">
            When you receive or send messages, they will appear here.
          </Text>
        </Flex>
      );
    }

    if (selectedJobId && !selectedUserId) {
      const selectedChatRoom = chatRooms.find((room) => room.jobId === selectedJobId);
      const users = new Set<string>();
      selectedChatRoom?.messages.forEach((msg) => {
        const otherUser = msg.sender === user_id ? msg.recipient : msg.sender;
        if (otherUser !== user_id) users.add(otherUser);
      });
      const userList = Array.from(users);

      return (
        <div className="h-full bg-white rounded-lg shadow-sm relative">
          <Flex align="center" className="p-4 border-b">
            {isMobile && (
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => setSelectedJobId(null)}
                className="!mr-2"
              />
            )}
            <Title level={4} className="!mb-0">
              Select a User
            </Title>
          </Flex>
          <div className="h-[calc(100vh-280px)] p-4 overflow-y-auto">
            {userList.map((userId) => {
              const unreadMessages = (selectedChatRoom?.messages.filter(
                (msg) => msg.sender === userId && msg.recipient === user_id && msg.status === "unread"
              ).length) || 0;

              return (
                <Card
                  key={userId}
                  onClick={() => setSelectedUserId(userId)}
                  className="!rounded-none !border-0 !cursor-pointer hover:!bg-gray-50"
                >
                  <Flex align="center" gap="middle">
                    <Avatar>{userNames[userId]?.[0]}</Avatar>
                    <Flex justify="space-between" className="w-full">
                      <Text>{userNames[userId] || "Loading..."}</Text>
                      {unreadMessages > 0 && (
                        <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {unreadMessages}
                        </div>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              );
            })}
          </div>
        </div>
      );
    }

    const selectedChatRoom = chatRooms.find((room) => room.jobId === selectedJobId);
    const filteredMessages =
      selectedChatRoom?.messages.filter(
        (msg) =>
          (msg.sender === user_id && msg.recipient === selectedUserId) ||
          (msg.sender === selectedUserId && msg.recipient === user_id)
      ) || [];
    const optimisticMessagesForCurrent = optimisticMessages.filter(
      (msg) =>
        msg.jobId === selectedJobId &&
        ((msg.sender === user_id && msg.recipient === selectedUserId) ||
          (msg.sender === selectedUserId && msg.recipient === user_id))
    );
    const messagesToDisplay = [...filteredMessages, ...optimisticMessagesForCurrent].sort(
      (a, b) => a.timestamp?.toMillis() - b.timestamp?.toMillis()
    );

    return (
      <div className="h-full bg-white rounded-lg shadow-sm relative">
        <Flex align="center" className="p-4 border-b">
          {isMobile && (
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => setSelectedUserId(null)}
              className="!mr-2"
            />
          )}
          <Title level={4} className="!mb-0">
            To: {userNames[selectedUserId]} for {selectedChatRoom?.jobTitle}
          </Title>
        </Flex>
        <div
          className={`h-[calc(100vh-280px)] p-6 ${isMobile ? "pb-6 mb-64" : ""} overflow-y-auto flex flex-col`}
        >
          {messagesToDisplay.map((msg, index) => (
            <Flex
              key={msg.id || index}
              justify={msg.sender === user_id ? "flex-end" : "flex-start"}
              className="mb-2"
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 leading-relaxed ${msg.sender === user_id ? "bg-blue-100 text-blue" : "bg-gray-100"
                  }`}
                style={{ marginBottom: "8px" }}
              >
                {msg.sender !== user_id && (
                  <Flex align="center" className="mb-1">
                    <Avatar size="small" className="!bg-primary-500 !text-white mr-2">
                      {userNames[msg.sender]?.[0]}
                    </Avatar>
                    <Text type="secondary" className="text-xs text-gray-500">
                      {userNames[msg.sender] || "Unknown"}
                    </Text>
                  </Flex>
                )}
                <Text className={msg.sender === user_id ? "text-blue" : ""}>{msg.text}</Text>
                <div className="text-xs mt-1 opacity-70">
                  {(() => {
                    const d = new Date(msg.timestamp?.toDate() || Date.now());
                    return Date.now() - d.getTime() > 86400000
                      ? d.toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  })()}
                </div>
              </div>
            </Flex>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <div className="absolute bottom-0 w-full p-4 bg-white border-t">
          <Flex gap="middle">
            <Input
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPressEnter={handleSendMessage}
              className="flex-1"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={loading}
              disabled={!message}
            />
          </Flex>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8} className={selectedJobId && isMobile ? "hidden" : "block"}>
            {renderUsers()}
          </Col>
          <Col xs={24} md={16} className={!selectedJobId && isMobile ? "hidden" : "block"}>
            {renderMessages()}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Messages;